/**
 * SQAAF Dashboard – School Quality Assessment and Accreditation Framework
 * Pune District analytics with URL-synced filters, equity views, and intervention tiers.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import SqafFilterBar from "@/components/sqaaf/SqafFilterBar";
import SqafBlockRankingChart from "@/components/sqaaf/SqafBlockRankingChart";
import SqafExecutiveOverview from "@/pages/SqafExecutiveOverview";
import SqafBlockPerformance from "@/pages/SqafBlockPerformance";
import SqafDomainSection from "@/pages/SqafDomainSection";
import SqafSchoolDiagnostics from "@/pages/SqafSchoolDiagnostics";
import SqafPolicyAction from "@/pages/SqafPolicyAction";
import { BAND_COLORS, SQAAF_NAV_ITEMS, BAND_THRESHOLDS } from "@/components/sqaaf/sqaafConstants";
import { SkeletonKPICard } from "@/components/PremiumSkeleton";
import {
  fmt2,
  getSectionLabel,
  getSectionLabelShort,
  getManagementLabel,
  getCategoryLabel,
} from "@/lib/sqaafLabels";
import { getBackendUrl } from "@/lib/backend";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  ReferenceLine,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";
import {
  ClipboardCheck,
  RefreshCw,
  School,
  Target,
  AlertTriangle,
  BarChart3,
  MapPin,
  Users,
  Download,
  Search,
  ChevronDown,
  Filter,
  ChevronRight,
} from "lucide-react";
import "./SqafDashboard.css";

const API = `${getBackendUrl()}/api`;

const NAV_ICONS = {
  executive: BarChart3,
  blocks: MapPin,
  sections: Target,
  equity: Users,
  schools: School,
  intervention: AlertTriangle,
};

const TAB_ITEMS = SQAAF_NAV_ITEMS.map((item) => ({
  ...item,
  icon: NAV_ICONS[item.id] || BarChart3,
}));

const HEADER_COPY = {
  executive: {
    eyebrow: "Executive overview · Pune District · SQAAF",
    subtitle: "District quality posture, band distribution, score histogram and narrative priorities.",
  },
  blocks: {
    eyebrow: "Block performance · Pune District · SQAAF",
    subtitle: "Block rankings, heatmaps, score spread and intervention-ready block comparisons.",
  },
  sections: {
    eyebrow: "Domain & section · Pune District · SQAAF",
    subtitle: "Section means, domain gaps, chapter summaries and lowest-performing questions.",
  },
  equity: {
    eyebrow: "Equity & segments · Pune District · SQAAF",
    subtitle: "Management and category equity views with completion and compliance signals.",
  },
  schools: {
    eyebrow: "School diagnostics · Pune District · SQAAF",
    subtitle: "School-level SQAAF scores, band filters, drill-down detail and export actions.",
  },
  intervention: {
    eyebrow: "Policy action · Pune District · SQAAF",
    subtitle: "Intervention tiers, priority domains, critical schools and block intervention index.",
  },
};

const TIER_OPTIONS = [
  { id: "critical", label: "Critical" },
  { id: "needs_focus", label: "Needs Focus" },
  { id: "below_60", label: "Below 60%" },
];

function downloadCSV(rows, filename) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function truncateQuestion(text, maxLen = 52) {
  if (!text || typeof text !== "string") return "";
  const t = text.trim();
  return t.length <= maxLen ? t : `${t.slice(0, maxLen).trim()}…`;
}

function interventionIndexColor(index) {
  const v = Number(index);
  if (v > 35) return "#ef4444";
  if (v >= 25) return "#f59e0b";
  return "#22c55e";
}

function mapSchoolSortParam(sort) {
  if (sort === "Answered_%") return "answered";
  if (sort === "SQAAF_Score_%") return "score";
  if (sort === "school_name") return "school_name";
  if (sort === "block") return "block";
  return sort;
}

function buildFilterParams(block, cluster, management, category) {
  return {
    block: block || undefined,
    cluster: cluster || undefined,
    school_management: management || undefined,
    school_category: category || undefined,
  };
}

function BottomQuestionsTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md max-w-sm">
      <p className="text-xs font-medium text-slate-500 mb-1">
        Mean: {row?.["mean_score_%"] != null ? `${fmt2(row["mean_score_%"])}%` : "—"}
      </p>
      {row?.section_label && <p className="text-xs text-slate-600 mb-1">{row.section_label}</p>}
      <p className="text-xs text-slate-700 whitespace-normal break-words">{row?.question}</p>
    </div>
  );
}

function chartHeightForRows(count, minHeight = 320, rowHeight = 36) {
  return Math.max(minHeight, (count || 1) * rowHeight);
}

function BoxplotRangeChart({ data }) {
  const chartData = (data || []).map((b) => {
    const min = Number(b.min ?? 0);
    const q1 = Number(b.q1 ?? 0);
    const q3 = Number(b.q3 ?? 0);
    const max = Number(b.max ?? 0);
    return {
      block: b.block,
      whiskerBase: min,
      lowerWhisker: Math.max(0, q1 - min),
      box: Math.max(0, q3 - q1),
      upperWhisker: Math.max(0, max - q3),
      median: Number(b.median ?? 0),
      min,
      max,
      q1,
      q3,
    };
  });

  const height = chartHeightForRows(chartData.length);

  return (
    <div className="w-full min-w-0" style={{ height }} aria-label="Block score distribution boxplot">
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tick={{ fontSize: 11 }}
          />
          <YAxis type="category" dataKey="block" width={100} tick={{ fontSize: 10 }} interval={0} />
          <RechartsTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload;
              return (
                <div className="rounded border bg-white p-2 text-xs shadow">
                  <p className="font-medium">{row.block}</p>
                  <p>Min: {fmt2(row.min)}%</p>
                  <p>Q1: {fmt2(row.q1)}%</p>
                  <p>Median: {fmt2(row.median)}%</p>
                  <p>Q3: {fmt2(row.q3)}%</p>
                  <p>Max: {fmt2(row.max)}%</p>
                </div>
              );
            }}
          />
          <Bar dataKey="whiskerBase" stackId="spread" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="lowerWhisker" stackId="spread" fill="#cbd5e1" isAnimationActive={false} />
          <Bar dataKey="box" stackId="spread" fill="#3b82f6" isAnimationActive={false} />
          <Bar dataKey="upperWhisker" stackId="spread" fill="#cbd5e1" radius={[0, 4, 4, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-slate-500 mt-2">Whiskers: min–max; box: Q1–Q3 (median in tooltip).</p>
    </div>
  );
}

const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description, tooltip, skeleton }) => {
  if (skeleton) return <SkeletonKPICard />;
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    cyan: "bg-cyan-50 text-cyan-600",
  };
  const card = (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">
                {typeof value === "number" ? fmt2(value) : value ?? "—"}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colors[color] || colors.blue}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
  if (!tooltip) return card;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{card}</div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-900 text-white">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

function BottomQuestionsTable({ questions }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead className="w-8" />
          <TableHead>Question</TableHead>
          <TableHead>Section</TableHead>
          <TableHead className="text-right">Mean %</TableHead>
          <TableHead className="text-right">Score ≤1 %</TableHead>
          <TableHead className="text-right">Coverage %</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(questions || []).map((q, i) => {
          const isOpen = expanded.has(i);
          const preview = truncateQuestion(q.question, 72);
          return (
            <TableRow key={i}>
              <TableCell>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggle(i)} aria-label={isOpen ? "Collapse question" : "Expand question"}>
                  <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </Button>
              </TableCell>
              <TableCell className="text-xs max-w-lg">
                {isOpen ? q.question : preview}
              </TableCell>
              <TableCell className="text-xs">{q.section_label || q.section}</TableCell>
              <TableCell className="text-right tabular-nums">{fmt2(q["mean_score_%"])}</TableCell>
              <TableCell className="text-right tabular-nums">{fmt2(q.pct_score_le_1)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmt2(q.coverage_pct)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function EmptyFilteredState({ onClear }) {
  return (
    <Card className="border-slate-200 border-dashed">
      <CardContent className="py-16 text-center">
        <Filter className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <h3 className="text-lg font-semibold text-slate-700">No schools match these filters</h3>
        <p className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
          Adjust block, cluster, management, or category filters to see SQAAF analytics for a cohort.
        </p>
        <Button variant="outline" className="mt-4" onClick={onClear}>
          Clear all filters
        </Button>
      </CardContent>
    </Card>
  );
}

const SqafDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const blockFilter = searchParams.get("block") || "";
  const clusterFilter = searchParams.get("cluster") || "";
  const managementFilter = searchParams.get("school_management") || "";
  const categoryFilter = searchParams.get("school_category") || "";
  const activeTab = searchParams.get("tab") || "executive";
  const schoolFromUrl = searchParams.get("school") || "";
  const tier = searchParams.get("tier") || "critical";

  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);

  const [overview, setOverview] = useState(null);
  const [blockPerf, setBlockPerf] = useState(null);
  const [sectionAnalysis, setSectionAnalysis] = useState(null);
  const [schoolsList, setSchoolsList] = useState({ schools: [], total: 0 });
  const [intervention, setIntervention] = useState(null);
  const [mgmtCategory, setMgmtCategory] = useState(null);
  const [questionCoverage, setQuestionCoverage] = useState(null);
  const [schoolDetail, setSchoolDetail] = useState(null);

  const [schoolSearch, setSchoolSearch] = useState("");
  const [schoolBand, setSchoolBand] = useState("");
  const [schoolSort, setSchoolSort] = useState("SQAAF_Score_%");
  const [schoolPage, setSchoolPage] = useState(0);
  const [selectedSchoolCode, setSelectedSchoolCode] = useState(schoolFromUrl);
  const [blockSort, setBlockSort] = useState({ key: "mean", dir: "desc" });
  const [scoringOpen, setScoringOpen] = useState(false);
  const [moreMetricsOpen, setMoreMetricsOpen] = useState(false);
  const [schoolExportLoading, setSchoolExportLoading] = useState(false);
  const schoolDetailRequestRef = useRef(0);
  const [lowestQBand, setLowestQBand] = useState("Critical");
  const [lowestQBlock, setLowestQBlock] = useState("");

  const filterParams = useMemo(
    () => buildFilterParams(blockFilter, clusterFilter, managementFilter, categoryFilter),
    [blockFilter, clusterFilter, managementFilter, categoryFilter]
  );

  const patchParams = useCallback(
    (updates, replace = true) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === "" || v == null) next.delete(k);
        else next.set(k, String(v));
      });
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    patchParams({
      block: "",
      cluster: "",
      school_management: "",
      school_category: "",
    });
  }, [patchParams]);

  const fetchOverview = useCallback(async () => {
    setOverviewError(null);
    try {
      const res = await axios.get(`${API}/sqaaf/overview`, { params: filterParams });
      setOverview(res.data);
      return res.data;
    } catch (e) {
      const msg = e.response?.data?.detail ?? e.message ?? "Failed to load SQAAF overview";
      setOverviewError(Array.isArray(msg) ? msg.join(" ") : String(msg));
      setOverview(null);
      return null;
    }
  }, [filterParams]);

  const fetchBlockPerf = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sqaaf/block-performance`, { params: filterParams });
      setBlockPerf(res.data);
    } catch {
      setBlockPerf(null);
    }
  }, [filterParams]);

  const fetchSectionAnalysis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sqaaf/section-analysis`, { params: filterParams });
      setSectionAnalysis(res.data);
    } catch {
      setSectionAnalysis(null);
    }
  }, [filterParams]);

  const fetchMgmtCategory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sqaaf/management-category`, { params: filterParams });
      setMgmtCategory(res.data);
    } catch {
      setMgmtCategory(null);
    }
  }, [filterParams]);

  const fetchQuestionCoverage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sqaaf/question-coverage`, { params: filterParams });
      setQuestionCoverage(res.data);
    } catch {
      setQuestionCoverage(null);
    }
  }, [filterParams]);

  const fetchSchools = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sqaaf/schools`, {
        params: {
          ...filterParams,
          band: schoolBand || undefined,
          search: schoolSearch || undefined,
          sort_by: mapSchoolSortParam(schoolSort) || undefined,
          sort_order: "desc",
          limit: 50,
          offset: schoolPage * 50,
        },
      });
      setSchoolsList({ schools: res.data.schools, total: res.data.total });
    } catch {
      setSchoolsList({ schools: [], total: 0 });
    }
  }, [filterParams, schoolBand, schoolSearch, schoolSort, schoolPage]);

  const exportSchoolsCsv = useCallback(async () => {
    setSchoolExportLoading(true);
    try {
      const pageSize = 1000;
      let offset = 0;
      let total = 0;
      const allRows = [];
      do {
        const res = await axios.get(`${API}/sqaaf/schools`, {
          params: {
            ...filterParams,
            band: schoolBand || undefined,
            search: schoolSearch || undefined,
            sort_by: mapSchoolSortParam(schoolSort) || undefined,
            sort_order: "desc",
            limit: pageSize,
            offset,
          },
        });
        total = res.data.total ?? 0;
        const page = res.data.schools || [];
        allRows.push(
          ...page.map(({ question_scores, ...rest }) => rest)
        );
        offset += page.length;
        if (!page.length) break;
      } while (offset < total);
      if (!allRows.length) {
        toast.error("No schools to export for the current filters");
        return;
      }
      downloadCSV(allRows, "sqaaf_schools.csv");
      toast.success(`Exported ${allRows.length.toLocaleString()} schools`);
    } catch {
      toast.error("Failed to export schools");
    } finally {
      setSchoolExportLoading(false);
    }
  }, [filterParams, schoolBand, schoolSearch, schoolSort]);

  const fetchIntervention = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sqaaf/intervention`, {
        params: {
          ...filterParams,
          block: lowestQBlock || filterParams.block || undefined,
          tier:
            lowestQBand === "Needs Focus"
              ? "needs_focus"
              : lowestQBand === "Developing"
                ? "below_60"
                : tier,
          limit: 500,
          offset: 0,
        },
      });
      setIntervention(res.data);
    } catch {
      setIntervention(null);
    }
  }, [filterParams, tier, lowestQBand, lowestQBlock]);

  const fetchSchoolDetail = useCallback(async (code) => {
    if (!code) {
      schoolDetailRequestRef.current += 1;
      setSchoolDetail(null);
      return;
    }
    const requestId = ++schoolDetailRequestRef.current;
    setSchoolDetail((prev) => (prev?.school?.school_code === code ? prev : null));
    try {
      const res = await axios.get(`${API}/sqaaf/school-detail/${encodeURIComponent(code)}`);
      if (requestId !== schoolDetailRequestRef.current) return;
      setSchoolDetail(res.data);
    } catch {
      if (requestId !== schoolDetailRequestRef.current) return;
      toast.error("School detail not found");
      setSchoolDetail(null);
    }
  }, []);

  const fetchAll = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent && !overview) setInitialLoading(true);
      else setRefreshing(true);
      await Promise.all([
        fetchOverview(),
        fetchBlockPerf(),
        fetchSectionAnalysis(),
        fetchMgmtCategory(),
        fetchQuestionCoverage(),
        fetchIntervention(),
      ]);
      if (activeTab === "schools") await fetchSchools();
      setInitialLoading(false);
      setRefreshing(false);
    },
    [
      overview,
      fetchOverview,
      fetchBlockPerf,
      fetchSectionAnalysis,
      fetchMgmtCategory,
      fetchQuestionCoverage,
      fetchIntervention,
      fetchSchools,
      activeTab,
    ]
  );

  const runImport = useCallback(async () => {
    setImportLoading(true);
    setOverviewError(null);
    try {
      const res = await axios.post(`${API}/sqaaf/import`);
      toast.success(`Loaded ${res.data?.n_schools ?? 0} schools into MongoDB`);
      await fetchAll();
    } catch (e) {
      const msg = e.response?.data?.detail ?? e.message ?? "Import failed";
      setOverviewError(Array.isArray(msg) ? msg.join(" ") : String(msg));
      toast.error("SQAAF import failed");
    } finally {
      setImportLoading(false);
    }
  }, [fetchAll]);

  useEffect(() => {
    fetchAll({ silent: !!overview });
  }, [blockFilter, clusterFilter, managementFilter, categoryFilter, tier, lowestQBand, lowestQBlock]);

  useEffect(() => {
    if (activeTab === "schools") fetchSchools();
  }, [activeTab, fetchSchools]);

  useEffect(() => {
    if (activeTab !== "schools") return;
    if (!selectedSchoolCode && schoolsList.schools?.length) {
      setSelectedSchoolCode(schoolsList.schools[0].school_code);
    }
  }, [activeTab, schoolsList.schools, selectedSchoolCode]);

  useEffect(() => {
    if (!selectedSchoolCode) return;
    if (selectedSchoolCode !== schoolFromUrl) {
      patchParams({ school: selectedSchoolCode, tab: "schools" });
    }
    fetchSchoolDetail(selectedSchoolCode);
  }, [selectedSchoolCode, schoolFromUrl, fetchSchoolDetail, patchParams]);

  useEffect(() => {
    if (schoolFromUrl && schoolFromUrl !== selectedSchoolCode) {
      setSelectedSchoolCode(schoolFromUrl);
    }
  }, [schoolFromUrl]);

  const noMongoData =
    !!overviewError && /no sqaaf/i.test(String(overviewError));
  const emptyFilter = overview && Number(overview.n_schools) === 0;
  const criticalCountDisplay =
    overview?.band_counts?.Critical ??
    intervention?.intervention_summary?.critical_count ??
    0;
  const schoolTotalPages = Math.max(1, Math.ceil((schoolsList.total || 0) / 50));

  const sortedBlockSummary = useMemo(() => {
    const rows = [...(blockPerf?.block_summary || [])];
    const { key, dir } = blockSort;
    rows.sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      return dir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [blockPerf, blockSort]);

  const mgmtChartData = useMemo(
    () =>
      (mgmtCategory?.management_summary || []).map((r) => ({
        ...r,
        label: getManagementLabel(r.school_management),
        name: r.school_management,
      })),
    [mgmtCategory]
  );

  const catChartData = useMemo(
    () =>
      (mgmtCategory?.category_summary || []).map((r) => ({
        ...r,
        label: getCategoryLabel(r.school_category),
        name: r.school_category,
      })),
    [mgmtCategory]
  );

  const priorityChartData = useMemo(() => {
    const dm = sectionAnalysis?.district_mean ?? overview?.district_mean ?? 0;
    return (intervention?.priority_domains || []).map((d) => ({
      section: d.section,
      label: d.section_label || getSectionLabelShort(d.section),
      mean: d.mean,
      district_mean: dm,
    }));
  }, [intervention, sectionAnalysis, overview]);

  const toggleBlockSort = (key) => {
    setBlockSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
    );
  };

  const applyBlockFilter = (blockName) => {
    if (!blockName) return;
    patchParams({ block: blockName, cluster: "" });
  };

  const showSkeleton = refreshing && overview;

  const exportExecutiveCsv = useCallback(() => {
    const rows = [
      {
        district_mean: overview?.district_mean,
        district_median: overview?.district_median,
        n_schools: overview?.n_schools,
        intervention_load: overview?.intervention_load,
        equity_gap: overview?.equity_gap,
        block_gap: overview?.block_gap_display,
        completion_rate: overview?.completion_rate,
      },
    ];
    downloadCSV(rows, "sqaaf_executive_overview.csv");
    toast.success("Executive overview exported");
  }, [overview]);

  const navigateTab = useCallback(
    (tabId) => {
      patchParams({ tab: tabId });
    },
    [patchParams]
  );

  const exportBlocksCsv = useCallback(() => {
    downloadCSV(blockPerf?.block_summary || [], "sqaaf_block_summary.csv");
    toast.success("Block summary exported");
  }, [blockPerf]);

  const exportSectionsCsv = useCallback(() => {
    const rows = sectionAnalysis?.section_means_sorted || [];
    downloadCSV(rows, "sqaaf_section_summary.csv");
    toast.success("Section summary exported");
  }, [sectionAnalysis]);

  const exportPolicyCsv = useCallback(() => {
    const rows = intervention?.critical_schools || intervention?.tier_schools || [];
    if (!rows.length) {
      toast.error("No critical schools to export");
      return;
    }
    downloadCSV(rows, "sqaaf_policy_critical_schools.csv");
    toast.success(`Exported ${rows.length.toLocaleString()} critical schools`);
  }, [intervention]);

  const openSchoolDiagnostics = useCallback(
    (schoolCode) => {
      if (!schoolCode) return;
      setSelectedSchoolCode(schoolCode);
      patchParams({ tab: "schools", school: schoolCode });
    },
    [patchParams]
  );

  if (initialLoading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (noMongoData) {
    return (
      <div className="content" data-testid="sqaaf-dashboard">
        <div className="sqaf-empty-state">
          <ClipboardCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No SQAAF Data Available</h3>
          <p className="text-slate-500 mb-2 max-w-xl mx-auto">
            Load the Excel file once using the button below (or run ETL:{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">POST /api/sqaaf/import</code>).
          </p>
          <Button className="mt-4" onClick={runImport} disabled={importLoading}>
            {importLoading ? "Loading…" : "Load from Excel (ETL)"}
          </Button>
          {overviewError && (
            <div className="mt-4 max-w-xl mx-auto text-left">
              <p className="text-sm font-medium text-amber-800 mb-1">Error:</p>
              <p className="text-sm text-amber-700">{overviewError}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const renderExecutive = () => (
    <SqafExecutiveOverview
      overview={overview}
      blockPerf={blockPerf}
      intervention={intervention}
      showSkeleton={showSkeleton}
      refreshing={refreshing}
      onRefresh={() => fetchAll({ silent: true })}
      onExport={exportExecutiveCsv}
      onNavigateTab={navigateTab}
      lowestQuestions={intervention?.bottom_questions || overview?.bottom_questions || []}
      tableBandFilter={lowestQBand}
      onTableBandFilterChange={setLowestQBand}
      tableBlockFilter={lowestQBlock}
      onTableBlockFilterChange={setLowestQBlock}
      blockOptions={blockPerf?.block_summary?.map((b) => b.block) || []}
    />
  );

  const renderBlocks = () => (
    <SqafBlockPerformance
      overview={overview}
      blockPerf={blockPerf}
      showSkeleton={showSkeleton}
      refreshing={refreshing}
      onRefresh={() => fetchAll({ silent: true })}
      onExport={exportBlocksCsv}
      onDownloadCsv={exportBlocksCsv}
      onNavigateTab={navigateTab}
      onBlockClick={applyBlockFilter}
    />
  );

  const renderSections = () => (
    <SqafDomainSection
      overview={overview}
      sectionAnalysis={sectionAnalysis}
      showSkeleton={showSkeleton}
      refreshing={refreshing}
      onRefresh={() => fetchAll({ silent: true })}
      onExport={exportSectionsCsv}
      onDownloadCsv={exportSectionsCsv}
    />
  );

  const renderEquity = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">By school management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mgmtChartData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="label" type="category" width={95} tick={{ fontSize: 10 }} />
                  <RechartsTooltip formatter={(v) => [`${fmt2(v)}%`, "Mean"]} />
                  <Bar dataKey="mean" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">By school category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={catChartData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="label" type="category" width={95} tick={{ fontSize: 10 }} />
                  <RechartsTooltip formatter={(v) => [`${fmt2(v)}%`, "Mean"]} />
                  <Bar dataKey="mean" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Segment bubble chart</CardTitle>
          <CardDescription>X: mean score · Y: school count · Size: % critical</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ left: 12, right: 24, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" dataKey="mean" name="Mean %" domain={[0, 100]} />
                <YAxis type="number" dataKey="schools" name="Schools" />
                <ZAxis type="number" dataKey="critical_pct" range={[80, 800]} name="% Critical" />
                <RechartsTooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload;
                    return (
                      <div className="rounded border bg-white p-2 text-xs shadow">
                        <p className="font-medium">{p.label}</p>
                        <p>Mean: {fmt2(p.mean)}%</p>
                        <p>Schools: {p.schools}</p>
                        <p>Critical: {fmt2(p.critical_pct)}%</p>
                      </div>
                    );
                  }}
                />
                <Scatter name="Management" data={mgmtChartData} fill="#3b82f6" />
                <Scatter name="Category" data={catChartData} fill="#a855f7" />
                <Legend />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSchools = () => (
    <SqafSchoolDiagnostics
      overview={overview}
      schoolsList={schoolsList}
      schoolDetail={schoolDetail}
      selectedSchoolCode={selectedSchoolCode}
      showSkeleton={showSkeleton}
      refreshing={refreshing}
      blockFilter={blockFilter}
      managementFilter={managementFilter}
      schoolSearch={schoolSearch}
      schoolBand={schoolBand}
      schoolSort={schoolSort}
      schoolPage={schoolPage}
      schoolTotalPages={schoolTotalPages}
      schoolExportLoading={schoolExportLoading}
      onRefresh={() => fetchAll({ silent: true })}
      onExport={exportSchoolsCsv}
      onSearchChange={setSchoolSearch}
      onBandChange={(v) => {
        setSchoolBand(v);
        setSchoolPage(0);
      }}
      onSortChange={(v) => {
        setSchoolSort(v);
        setSchoolPage(0);
      }}
      onSchoolSelect={setSelectedSchoolCode}
      onSearchSubmit={() => {
        setSchoolPage(0);
        fetchSchools();
      }}
      onPageChange={(p) => setSchoolPage(Math.max(0, p))}
    />
  );

  const renderIntervention = () => (
    <SqafPolicyAction
      overview={overview}
      intervention={intervention}
      showSkeleton={showSkeleton}
      refreshing={refreshing}
      onRefresh={() => fetchAll({ silent: true })}
      onExport={exportPolicyCsv}
      onBlockClick={applyBlockFilter}
      onSchoolSelect={openSchoolDiagnostics}
      blockOptions={blockPerf?.block_summary?.map((b) => b.block) || []}
    />
  );

  const copy = HEADER_COPY[activeTab] || HEADER_COPY.executive;

  const tabRenderers = {
    executive: renderExecutive,
    blocks: renderBlocks,
    sections: renderSections,
    equity: renderEquity,
    schools: renderSchools,
    intervention: renderIntervention,
  };
  const renderActiveTab = tabRenderers[activeTab] || tabRenderers.executive;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="content animate-fade-in" data-testid="sqaaf-dashboard">
        {activeTab !== "executive" && activeTab !== "blocks" && activeTab !== "sections" && activeTab !== "schools" && activeTab !== "intervention" ? (
          <section className="page-header">
            <div>
              <div className="eyebrow">{copy.eyebrow}</div>
              <h1>{TAB_ITEMS.find((t) => t.id === activeTab)?.label || "SQAAF Dashboard"}</h1>
              <p>{copy.subtitle}</p>
            </div>
            <div className="header-actions">
              <Button variant="outline" size="sm" onClick={runImport} disabled={importLoading}>
                {importLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {importLoading ? "Importing…" : "Reload Excel"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => fetchAll({ silent: true })} disabled={refreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </section>
        ) : null}

        {activeTab !== "blocks" && activeTab !== "sections" && activeTab !== "schools" && activeTab !== "intervention" ? (
          <SqafFilterBar
            blockFilter={blockFilter}
            clusterFilter={clusterFilter}
            managementFilter={managementFilter}
            categoryFilter={categoryFilter}
            onBlockChange={(v) => patchParams({ block: v, cluster: "" })}
            onClusterChange={(v) => patchParams({ cluster: v })}
            onManagementChange={(v) => patchParams({ school_management: v })}
            onCategoryChange={(v) => patchParams({ school_category: v })}
            onClear={clearAllFilters}
            filterContext={overview?.filter_context || { n_schools: overview?.n_schools }}
            nQuestions={overview?.n_questions || 128}
          />
        ) : null}

        {emptyFilter ? (
          <EmptyFilteredState onClear={clearAllFilters} />
        ) : (
          <div className="sqaf-tab-panel" role="tabpanel">
            {renderActiveTab()}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default SqafDashboard;
